<template>
  <div class="border bg-light px-3 py-2 rounded-3 shadow-sm mb-4">
    <div class="row">
      <div class="col-9">
        <strong>{{ item.name }}</strong>
        <span
          v-if="currentQuantity && currentQuantity > 1"
          class="ms-1 text-muted"
          >× {{ currentQuantity }}</span
        >
        <small class="d-block">{{
          truncatedText(item.description || '')
        }}</small>

        <div v-if="item.type === 'product'" class="mt-2">
          <div class="text-muted small d-flex align-items-center">
            <IconBox class="me-1" />
            <span>Product</span>
            <span v-if="item.metadata.linked_booking" class="ms-2 text-primary">
              (Linked to booking)
            </span>
          </div>
          <div
            v-if="(item.metadata as any).unit_price"
            class="text-muted small"
          >
            Unit price: {{ toCash((item.metadata as any).unit_price) }}
          </div>
          <div v-if="item.metadata.max_quantity" class="text-muted small">
            Max quantity: {{ item.metadata.max_quantity }}
          </div>
          <div class="text-muted small">
            Tax: {{ item.metadata.apply_tax ? 'Applied' : 'Not applied' }}
          </div>
          <div
            v-if="
              item.metadata.max_quantity &&
              currentQuantity >= item.metadata.max_quantity
            "
            class="text-warning small mt-1"
          >
            <IconExclamationCircle class="me-1" />
            Maximum quantity reached
          </div>
          <div
            v-else-if="
              item.metadata.max_quantity &&
              currentQuantity < item.metadata.max_quantity
            "
            class="text-info small mt-1"
          >
            <IconInfoCircle class="me-1" />
            {{ item.metadata.max_quantity - currentQuantity }} more available
          </div>
        </div>

        <div
          v-if="item.metadata && item.metadata.type === 'booking'"
          class="mt-2"
        >
          <div
            v-if="item.metadata.start_time && item.metadata.end_time"
            class="text-muted small d-flex align-items-center"
          >
            <IconCalendar class="me-1" />
            <span>{{
              formatBookingDateTime(
                item.metadata.start_time,
                item.metadata.end_time
              )
            }}</span>
          </div>
          <div
            v-if="item.metadata.number_of_guests > 0"
            class="text-muted small d-flex align-items-center"
          >
            <IconUsers class="me-1" />
            <span>
              {{ item.metadata.number_of_guests }} guest{{
                item.metadata.number_of_guests > 1 ? 's' : ''
              }}
            </span>
          </div>
        </div>

        <div
          v-if="
            item.metadata &&
            item.metadata.type === 'program' &&
            programEventDate
          "
          class="mt-2"
        >
          <template v-if="typeof programEventDate === 'string'">
            <div class="text-muted small d-flex align-items-center">
              <IconCalendar class="me-1" />
              <span>{{ programEventDate }}</span>
            </div>
          </template>
          <template
            v-else-if="programEventDate && 'occurrences' in programEventDate"
          >
            <ProgramScheduleTimeline
              :occurrences="programEventDate.occurrences"
            />
          </template>
          <template v-else-if="programEventDate">
            <div class="text-muted small d-flex align-items-start">
              <IconCalendar class="me-1 mt-1" style="flex-shrink: 0" />
              <div class="d-flex flex-column">
                <span>{{ programEventDate.start }}</span>
                <span v-if="programEventDate.end">
                  → {{ programEventDate.end }}
                </span>
              </div>
            </div>
          </template>
        </div>

        <div v-if="participantName" class="mt-2 mb-2">
          <div class="text-muted small d-flex align-items-center">
            <IconUser class="me-1" />
            <span>Participant: {{ participantName }}</span>
          </div>
        </div>

        <FormSpinbuttonBase
          v-if="item.metadata.is_event || item.type === 'product'"
          :model-value="currentQuantity"
          :disabled="!item.metadata.can_edit_quantity && !loadingState"
          :min="1"
          :max="computeMaxQuantity(item)"
          @update:model-value="(val) => onQuantityUpdate(item, val)"
        />
        <small
          v-if="
            (item.metadata.is_event || item.type === 'product') &&
            quantityAtCap()
          "
          class="text-danger d-block mt-1"
        >
          {{ capMessage(item) }}
        </small>
      </div>
      <div class="col-3 text-end align-items-end d-flex flex-column">
        <a
          href="#"
          class="text-primary"
          @click.prevent="
            onRemoveItem({ uuid: item.uuid, id: Number(item.id) })
          "
        >
          <IconTimes />
        </a>
        <span v-if="item.id" class="mt-auto pb-1">
          {{ toCash(item.amount * (currentQuantity ?? 1)) }}
          {{
            item.metadata &&
            item.metadata.recurring &&
            item.metadata.recurring.interval === 'month'
              ? ' monthly'
              : item.metadata.recurring &&
                  item.metadata.recurring.interval === 'week'
                ? ' weekly'
                : ''
          }}
        </span>
      </div>
      <b-col v-if="msgExistsForCheckoutItem(Number(item.id))" cols="12">
        <small class="text-danger">
          {{ msgForCheckoutItem(Number(item.id)) }}
        </small>
      </b-col>
    </div>
  </div>
</template>

<script setup lang="ts">
import { FormSpinbuttonBase } from '@/components/base/Form';
import { ProgramScheduleTimeline } from '@/components/feature/Programs';
import {
  IconBox,
  IconCalendar,
  IconExclamationCircle,
  IconInfoCircle,
  IconTimes,
  IconUser,
  IconUsers,
} from '@/components/ui/Icon';
import { useMoney } from '@/composables/useMoney';
import AdditionalMember from '@/models/AdditionalMember';
import type { IProgramOccurrence } from '@/models/Program';
import truncate from 'lodash/truncate';
import { computed, onMounted, ref, watch } from 'vue';

const { toCash } = useMoney();

interface CartItemPayload {
  id: number;
  uuid: string;
}

interface RecurringMeta {
  end_english?: string;
  interval: string;
  start_english?: string;
}
interface ItemMetadata {
  additional_member?: { id: number };
  additional_member_id?: number;
  apply_tax?: boolean;
  can_edit_quantity?: boolean;
  end_time?: string;
  event_end_date?: string | null;
  event_start_date?: string | null;
  is_event?: boolean;
  linked_booking?: boolean;
  max_quantity?: number;
  number_of_guests?: number;
  occurrences?: IProgramOccurrence[];
  ongoing?: boolean;
  readable_event_end_date?: string | null;
  readable_event_end_time?: string | null;
  readable_event_start_date?: string | null;
  readable_event_start_time?: string | null;
  recurring?: RecurringMeta;
  start_time?: string;
  type?: string;
}
interface CartItemType {
  amount: number;
  description?: string;
  id: string | number;
  metadata: ItemMetadata;
  name: string;
  product_id?: number;
  quantity: number;
  type: string;
  uuid: string;
}

const { item, loadingState, msgExistsForCheckoutItem, msgForCheckoutItem } =
  defineProps<{
    item: CartItemType;
    loadingState: boolean;
    msgExistsForCheckoutItem: (id: number) => boolean;
    msgForCheckoutItem: (id: number) => string | null;
  }>();

const emit = defineEmits<{
  (e: 'remove', payload: CartItemPayload): void;
  (
    e: 'quantity-update',
    payload: { item: CartItemType; quantity: number }
  ): void;
}>();

const truncatedText = (text: string) =>
  truncate(text, { length: 50, omission: '...' });

const formatBookingDateTime = (startTime: string, endTime: string) => {
  if (!startTime || !endTime) return '';
  const start = new Date(startTime);
  const end = new Date(endTime);
  const startDate = start.toLocaleDateString();
  const startTimeStr = start.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  });
  const endTimeStr = end.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  });
  return `${startDate} | ${startTimeStr} - ${endTimeStr}`;
};

const formatFallbackProgramDateTime = (
  startTime?: string | null,
  endTime?: string | null
) => {
  if (!startTime) {
    return '';
  }

  const start = new Date(startTime);
  if (Number.isNaN(start.getTime())) {
    return '';
  }

  const formatDate = (value: Date) =>
    value.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  const formatTime = (value: Date) =>
    value.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  const hasTime = (value: Date) =>
    value.getHours() !== 0 || value.getMinutes() !== 0;

  if (!endTime) {
    return hasTime(start)
      ? `${formatDate(start)}, ${formatTime(start)}`
      : formatDate(start);
  }

  const end = new Date(endTime);
  if (Number.isNaN(end.getTime())) {
    return hasTime(start)
      ? `${formatDate(start)}, ${formatTime(start)}`
      : formatDate(start);
  }

  const startDate = formatDate(start);
  const endDate = formatDate(end);
  const sameDay = startDate === endDate;
  const startHasTime = hasTime(start);
  const endHasTime = hasTime(end);

  if (sameDay) {
    if (startHasTime && endHasTime && formatTime(start) !== formatTime(end)) {
      return `${startDate}, ${formatTime(start)} - ${formatTime(end)}`;
    }

    return startHasTime ? `${startDate}, ${formatTime(start)}` : startDate;
  }

  const startLabel = startHasTime
    ? `${startDate}, ${formatTime(start)}`
    : startDate;
  const endLabel = endHasTime ? `${endDate}, ${formatTime(end)}` : endDate;

  return `${startLabel} - ${endLabel}`;
};

type ProgramScheduleDisplay =
  | { end?: string; start: string }
  | { occurrences: IProgramOccurrence[] }
  | string
  | null;

const formatProgramEventDate = (
  metadata: ItemMetadata
): ProgramScheduleDisplay => {
  if (metadata.occurrences?.length) {
    return { occurrences: metadata.occurrences };
  }

  if (metadata.ongoing) {
    return 'Ongoing';
  }

  const startDate = metadata.readable_event_start_date;
  const endDate = metadata.readable_event_end_date;

  if (startDate) {
    if (!endDate || startDate === endDate) {
      return { start: startDate };
    }

    return { start: startDate, end: endDate };
  }

  const fallback = formatFallbackProgramDateTime(
    metadata.event_start_date,
    metadata.event_end_date
  );

  return fallback ? fallback : null;
};

const programEventDate = computed(() =>
  formatProgramEventDate(item.metadata || {})
);

const onRemoveItem = (payload: CartItemPayload) => emit('remove', payload);

const currentQuantity = ref<number>(item.quantity);
watch(
  () => item.quantity,
  (val) => {
    if (typeof val === 'number') currentQuantity.value = val;
  }
);

const onQuantityUpdate = (item: CartItemType, quantity: number) => {
  const max = computeMaxQuantity(item);
  const clamped = Math.max(1, Math.min(Number(quantity) || 1, max));
  currentQuantity.value = clamped;
  emit('quantity-update', { item, quantity: clamped });
};

const participantName = ref<string | null>(null);

onMounted(async () => {
  // Fetch participant name if additional_member is present
  const additionalMemberId =
    item.metadata?.additional_member?.id ?? item.metadata?.additional_member_id;

  if (additionalMemberId) {
    try {
      const member = await AdditionalMember.find(additionalMemberId);
      participantName.value = member.name;
    } catch (error) {
      console.error('Failed to fetch participant name:', error);
    }
  } else if (
    (item.metadata?.type === 'program' || item.metadata?.type === 'booking') &&
    (window as any).Spark?.user?.name
  ) {
    // If no additional_member, assume it's the logged-in user
    participantName.value = (window as any).Spark.user.name;
  }
});

const computeMaxQuantity = (item: CartItemType): number => {
  const meta = (item?.metadata || {}) as Record<string, unknown>;
  const candidates: Array<number | null | undefined> = [
    meta.max_quantity as number | null | undefined,
    (meta as any).num_signups_remaining_for_user as number | null | undefined,
    (meta as any).spots_remaining as number | null | undefined,
  ];
  const finite = candidates
    .map((v) => (typeof v === 'string' ? Number(v) : v))
    .filter(
      (v) => typeof v === 'number' && isFinite(v) && (v as number) > 0
    ) as number[];
  if (finite.length) {
    return Math.max(1, Math.min(...finite, 99));
  }
  return 99;
};

const quantityAtCap = (): boolean => {
  const max = computeMaxQuantity(item);
  return (Number(currentQuantity.value) || 1) >= max;
};

const capMessage = (item: CartItemType): string => {
  const meta: any = item?.metadata || {};
  const perUser = meta.num_signups_remaining_for_user;
  const remaining = meta.spots_remaining;
  const productMax = meta.max_quantity;
  if (typeof perUser === 'number' && perUser >= 0) {
    return `Maximum allowed per user is ${perUser}.`;
  }
  if (typeof remaining === 'number') {
    return `Only ${remaining} spot${remaining === 1 ? '' : 's'} remaining.`;
  }
  if (typeof productMax === 'number') {
    return `Maximum quantity is ${productMax}.`;
  }
  return 'Maximum quantity reached.';
};

defineOptions({ name: 'CartItem' });
</script>
